import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton,
    Typography, Divider, CircularProgress, Box, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SessionTieredPricingApi } from '../../../apis/SessionTieredPricing';
import { formatDateNoHour, formatHour } from '../../../utilities/generalUtils';

function formatDuration(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
}

const PhaseRow = ({ phase, isLast }) => (
    <>
        <Box sx={{ py: 1.5 }}>
            <Typography variant="body1" sx={{ fontSize: '1.05rem', mb: 0.5 }} color="text.secondary">
                {formatHour(phase.startTime)} – {formatHour(phase.endTime)}
                {' '}
                <span style={{ fontStyle: 'italic' }}>({formatDuration(phase.durationSeconds)})</span>
            </Typography>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2}>
                    <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>
                        ${(parseFloat(phase.kwhPrice) / 100).toFixed(2)}/kWh
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>
                        {parseFloat(phase.totalKwhDelivered).toFixed(4)} kWh
                    </Typography>
                </Stack>
                <Typography variant="body1" sx={{ fontSize: '1.15rem' }} fontWeight={600}>
                    ${(parseFloat(phase.totalCost) / 100).toFixed(2)}
                </Typography>
            </Stack>
        </Box>
        {!isLast && <Divider />}
    </>
);

const TieredPricingModal = ({ session, onClose }) => {
    const [phases, setPhases] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const open = !!session;
    const s = session?.session ?? {};

    useEffect(() => {
        if (!s.uuid) return;
        setLoading(true);
        setError('');
        setPhases(null);
        SessionTieredPricingApi.get(s.uuid)
            .then(data => setPhases(data.phasePricing ?? []))
            .catch(() => setError('Could not load pricing breakdown.'))
            .finally(() => setLoading(false));
    }, [s.uuid]);

    const totalCost = phases?.reduce((sum, p) => sum + parseFloat(p.totalCost) / 100, 0);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ pr: 6 }}>
                Session Breakdown
                <Typography variant="body1" sx={{ fontSize: '1.05rem' }} color="text.secondary">
                    {formatDateNoHour(s.startedAt)}
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: 'absolute', top: 12, right: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {error && (
                    <Typography color="error" variant="body2" sx={{ py: 1 }}>
                        {error}
                    </Typography>
                )}
                {!loading && !error && phases?.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        No pricing breakdown available for this session.
                    </Typography>
                )}
                {!loading && !error && phases?.length > 0 && (
                    <>
                        {phases.map((phase, idx) => (
                            <PhaseRow
                                key={idx}
                                phase={phase}
                                isLast={idx === phases.length - 1}
                            />
                        ))}
                        {phases.length > 1 && (
                            <>
                                <Divider sx={{ mt: 0.5 }} />
                                <Stack direction="row" justifyContent="space-between" sx={{ pt: 1.5 }}>
                                    <Typography variant="body1" fontWeight={600}>Total</Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        ${totalCost.toFixed(2)}
                                    </Typography>
                                </Stack>
                            </>
                        )}
                        {Math.round(totalCost * 100) > 0 && totalCost < 0.5 && (
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ mt: 1.5, fontWeight: 700 }}
                            >
                                No amount charged to your payment method.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TieredPricingModal;
